import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryClient";
import {
  fetchJobsPaginated,
  fetchMonthlyTLedger,
  fetchAgents,
  fetchServiceRates,
  fetchMonthlyPayoutSummary,
  createJob,
  updateJob,
  deleteJob,
  createLedgerEntry,
  deleteLedgerEntry,
  Job,
  JobPayload,
  FetchJobsParams,
  StaffJobItem,
  fetchStaffJobs,
  fetchMonthlyLedger,
} from "../../services/jobService";
import {
  fetchSuperAdminFinanceOverview,
  fetchStaffFinanceOverview,
  executeCommissionDisbursement,
  fetchPayrollRoster,
  fetchFinanceSummary,
  DisburseCommissionPayload,
} from "../../services/paymentFinanceService";

/**
 * Query hook for staff jobs paginated list
 */
export const useStaffJobsQuery = (params?: FetchJobsParams) => {
  return useQuery({
    queryKey: queryKeys.staffJobs.list(params),
    queryFn: ({ signal }) => fetchJobsPaginated({ ...params, signal }),
  });
};

/**
 * Query hook for monthly financial ledger
 */
export const useMonthlyLedgerQuery = (month?: string, scope?: string) => {
  return useQuery({
    queryKey: queryKeys.monthlyLedger.list({ month, scope }),
    queryFn: ({ signal }) => fetchMonthlyTLedger(month, scope, signal),
  });
};

/**
 * Mutation hook to create a ledger entry
 */
export const useCreateLedgerEntryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      entry_type: "EXPENSE" | "INCOME";
      item_name: string;
      amount: number | string;
      date?: string;
      notes?: string;
    }) => createLedgerEntry(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.monthlyLedger.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.all });
    },
  });
};

/**
 * Mutation hook to delete a ledger entry
 */
export const useDeleteLedgerEntryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteLedgerEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.monthlyLedger.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.all });
    },
  });
};

/**
 * Query hook for agents list
 */
export const useAgentsQuery = () => {
  return useQuery({
    queryKey: queryKeys.agents.list(),
    queryFn: ({ signal }) => fetchAgents(signal),
  });
};

/**
 * Query hook for service rates list
 */
export const useServiceRatesQuery = (showAll: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.serviceRates.list(showAll),
    queryFn: ({ signal }) => fetchServiceRates(showAll, signal),
  });
};

/**
 * Query hook for monthly payout summary
 */
export const useMonthlyPayoutSummaryQuery = (month?: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.staffJobs.payoutSummary(month),
    queryFn: ({ signal }) => fetchMonthlyPayoutSummary(month, signal),
    enabled,
  });
};

/**
 * Query hook for financial summary KPIs / treasury overview
 */
export const useFinanceSummaryQuery = (month?: string) => {
  return useQuery({
    queryKey: [...queryKeys.finance.summary(), month],
    queryFn: ({ signal }) => fetchSuperAdminFinanceOverview(month, signal),
  });
};

/**
 * Query hook for payroll roster by month
 */
export const usePayrollRosterQuery = (month?: string) => {
  return useQuery({
    queryKey: [...queryKeys.finance.all, "payroll", month],
    queryFn: () => fetchPayrollRoster(month),
    enabled: Boolean(month),
  });
};

/**
 * Query hook for staff personal finance overview
 */
export const useStaffFinanceOverviewQuery = (month?: string) => {
  return useQuery({
    queryKey: [...queryKeys.finance.all, "staffPersonal", month],
    queryFn: () => fetchStaffFinanceOverview(month),
  });
};

/**
 * Mutation hook to create staff job
 */
export const useCreateStaffJobMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: JobPayload) => createJob(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.staffJobs.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.monthlyLedger.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.all });
    },
  });
};

/**
 * Mutation hook to update staff job
 */
export const useUpdateStaffJobMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<JobPayload> }) =>
      updateJob(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.staffJobs.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.monthlyLedger.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.all });
    },
  });
};

/**
 * Mutation hook to delete staff job
 */
export const useDeleteStaffJobMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, scope }: { id: number; scope?: 'personal' | 'all' }) =>
      deleteJob(id, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.staffJobs.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.monthlyLedger.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.all });
    },
  });
};

/**
 * Mutation hook to execute commission disbursement
 */
export const useDisburseCommissionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: DisburseCommissionPayload) =>
      executeCommissionDisbursement(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.staffJobs.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.monthlyLedger.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.all });
    },
  });
};

